import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assert } from '@/utils'
import { queryKeys } from '@/query-keys'
import { assertIsDeliverTxSuccess, broadcastIbcTransaction2, IBCTransferStatus } from '@/wallet-utils'
import { traceIbcStatus2 } from '@/wallet-utils/ibc/trace-ibc-status'
import { MutationParameters } from './types'
import { useSelectedDenom } from '../../../../contexts/denom'
import { useSelectedChain } from '@/contexts/wallet'

export type BroadcastSendTokenMutationMode = 'deposit' | 'withdraw'

export interface BroadcastSendTokenMutationReturnType {
  tx: DeliverTxResponse
  status: IBCTransferStatus
}

const useBroadcastIbcTransferMutation = ({ ibcTransferRaw, setIbcTransferTransaction }: MutationParameters) => {
  const { getSigningStargateClient, getAccount, getOfflineSigner, wallet } = useSelectedChain()

  const [denom] = useSelectedDenom()

  const queryClient = useQueryClient()

  const handleMutation = async (): Promise<BroadcastSendTokenMutationReturnType> => {
    assert(ibcTransferRaw, 'Unable to broadcast transaction without a signed transaction raw.')

    assert(wallet, `You are unable to send token without your ${denom} wallet instance.`)

    const [client, account] = await Promise.all([getSigningStargateClient(), getAccount()])
    const ibcParams = { client, denom, account, offlineSigner: getOfflineSigner(), wallet: wallet.name }
    const tx = await broadcastIbcTransaction2(ibcParams, ibcTransferRaw)

    assertIsDeliverTxSuccess(tx)

    const status = await traceIbcStatus2({ denom, tx })

    if (status === 'return-later') {
      return {
        tx: tx,
        status: 'return-later'
      }
    }

    // We'll handle this after balance is updated because gas fee is still consumed
    // @TODO: It's possible that we may have to wait for funds to be fully refunded
    // @TODO: Handle error - we probably need a flow for this
    if (status === 'timeout') {
      return {
        tx: tx,
        status: 'timeout'
      }
    }

    return {
      tx: tx,
      status: 'complete'
    }
  }

  const handleSuccess = async ({ tx }: BroadcastSendTokenMutationReturnType) => {
    setIbcTransferTransaction(tx)

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.transactionHistoryIbcStatusByHash({ hash: tx.transactionHash })
      }),
      queryClient.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })
    ])
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useBroadcastIbcTransferMutation }
